import { useCallback, useEffect, useRef, useState } from "react";
import type { LiveLogControlFrame, LiveLogLineFrame, LiveLogOptions } from "@k8s-dashboard/contracts";
import { HttpError } from "@/api/client";
import { streamLiveLogs } from "@/api/livelogs";

/**
 * 라이브 로그 세션 훅 (ADR 0021 · Issue #9)
 * --------------------------------------------------------------------------
 * 규칙
 * - **유계 창만 보관합니다.** 세션 전체를 React state에 쌓지 않습니다.
 * - **pause는 숨기기가 아니라 abort입니다.** 뒤에서 계속 쌓이는 스트림을 가리면
 *   메모리와 서버 연결이 모두 살아 있는 채로 사용자만 모르게 됩니다.
 * - 재접속은 **명시적**입니다. 자동 재시도는 하지 않습니다 — 재생성 Pod에
 *   자동으로 붙는 것과 구분되지 않게 됩니다.
 */

/** 화면에 유지하는 최대 줄 수. 넘으면 오래된 줄부터 버립니다. */
export const LIVE_LOG_WINDOW = 2000;

export type LiveLogStatus = "idle" | "connecting" | "streaming" | "ended" | "error";

export interface LiveLogSession {
  lines: LiveLogLineFrame[];
  /** 마지막으로 시작한 옵션. 재접속이 같은 대상인지 판단하는 근거입니다. */
  active?: LiveLogOptions;
  status: LiveLogStatus;
  control?: LiveLogControlFrame;
  error?: HttpError | Error;
  /** 창을 넘겨 버린 줄 수. 화면에 그대로 노출합니다. */
  dropped: number;
  /** 높이·파서 캐시가 seq 재사용을 새 세션으로 구분하는 세대입니다. */
  sessionId: number;
  start: (options: LiveLogOptions) => void;
  stop: () => void;
  clear: () => void;
}

export function useLiveLogs(clusterId: string, podName: string, ns: string, podUid: string, ownerIdentity = ""): LiveLogSession {
  const [lines, setLines] = useState<LiveLogLineFrame[]>([]);
  const [status, setStatus] = useState<LiveLogStatus>("idle");
  const [control, setControl] = useState<LiveLogControlFrame | undefined>();
  const [error, setError] = useState<HttpError | Error | undefined>();
  const [dropped, setDropped] = useState(0);
  const [active, setActive] = useState<LiveLogOptions | undefined>();
  const [sessionId, setSessionId] = useState(0);
  const abort = useRef<AbortController>();
  /* 다음 연결은 이전 stream promise가 끝난 뒤에만 시작합니다. abort 직후 fetch가
     아직 정리 중인 짧은 구간에도 두 pods/log 요청이 겹치지 않습니다. */
  const settled = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);
  /* 프레임은 초당 수백 개까지 올 수 있습니다. setState를 프레임마다 부르면
     렌더가 스트림을 따라가지 못합니다. 버퍼에 모아 프레임 단위로 반영합니다. */
  const pending = useRef<LiveLogLineFrame[]>([]);
  const flushTimer = useRef<number>();

  /** 창을 넘긴 만큼 앞에서 버리고 버린 수를 셉니다. 버퍼와 화면이 같은 규칙을 씁니다. */
  const trim = useCallback((rows: LiveLogLineFrame[]) => {
    const cut = rows.length - LIVE_LOG_WINDOW;
    if (cut <= 0) return rows;
    setDropped((d) => d + cut);
    return rows.slice(cut);
  }, []);

  const flush = useCallback(() => {
    flushTimer.current = undefined;
    const batch = pending.current;
    if (batch.length === 0) return;
    pending.current = [];
    setLines((prev) => trim(prev.concat(batch)));
  }, [trim]);

  const scheduleFlush = useCallback(() => {
    if (flushTimer.current !== undefined) return;
    flushTimer.current = window.setTimeout(flush, 100);
  }, [flush]);

  const stop = useCallback(() => {
    generation.current++;
    abort.current?.abort();
    if (flushTimer.current !== undefined) {
      window.clearTimeout(flushTimer.current);
      flushTimer.current = undefined;
    }
    flush();
    setStatus((s) => (s === "streaming" || s === "connecting" ? "ended" : s));
  }, [flush]);

  /** 세 곳(수동 지우기·새 세션 시작·Pod 변경)이 같은 초기화를 씁니다. */
  const clear = useCallback(() => {
    pending.current = [];
    setLines([]);
    setDropped(0);
    setControl(undefined);
    setError(undefined);
    setSessionId((value) => value + 1);
  }, []);

  const start = useCallback(
    (options: LiveLogOptions) => {
      const mine = ++generation.current;
      abort.current?.abort();
      clear();
      setActive(options);
      setStatus("connecting");
      const previous = settled.current.catch(() => undefined);
      const task = previous.then(async () => {
        if (mine !== generation.current || !clusterId || !podName || !ns || !podUid) return;
        const controller = new AbortController();
        abort.current = controller;
        try {
          await streamLiveLogs(
            clusterId,
            podName,
            ns,
            podUid,
            options,
            {
              onLine: (frame) => {
                if (mine !== generation.current) return;
                setStatus((s) => (s === "connecting" ? "streaming" : s));
                pending.current.push(frame);
                pending.current = trim(pending.current);
                scheduleFlush();
              },
              onControl: (frame) => {
                if (mine === generation.current) setControl(frame);
              },
            },
            controller.signal,
          );
          if (mine !== generation.current || controller.signal.aborted) return;
          flush();
          setStatus("ended");
        } catch (err: unknown) {
          if (mine !== generation.current || controller.signal.aborted) return;
          flush();
          setError(err instanceof Error ? err : new Error("라이브 로그 연결이 실패했습니다."));
          setStatus("error");
        } finally {
          if (abort.current === controller) abort.current = undefined;
        }
      });
      settled.current = task;
    },
    [clusterId, podName, ns, podUid, flush, scheduleFlush, trim, clear],
  );

  /* 화면을 떠나면 연결이 남지 않아야 합니다. */
  useEffect(() => () => {
    generation.current++;
    abort.current?.abort();
    if (flushTimer.current !== undefined) window.clearTimeout(flushTimer.current);
  }, []);

  /* Pod가 바뀌면 이전 세션을 이어 쓰지 않습니다 — UID가 신원입니다. */
  useEffect(() => {
    generation.current++;
    abort.current?.abort();
    clear();
    setActive(undefined);
    setStatus("idle");
  }, [clusterId, ns, podName, podUid, ownerIdentity, clear]);

  return { lines, status, control, error, dropped, active, sessionId, start, stop, clear };
}
